/**
 * landing — 랜딩 샘플 뷰어 API.
 *
 * 인증 불필요(비회원 접근 가능).
 * - GET /fo/landing/samples : 대표 대학 샘플 메타 목록(서버 정렬, 클라이언트 재정렬 금지) — 표지 주소(coverUrl) 포함
 * - GET /fo/samples/{universityId}/cover : 표지 JPEG(카드 <img> 가 그대로 소비 — 066)
 * - GET /fo/samples/{universityId}/stream : PDF 바이트 스트림(전체화면 뷰어의 pdf.js 만 소비)
 * - GET /fo/landing/universities : 커버 섹션 노출 대학 목록 + 샘플 보유 여부(silent, 실패 시 섹션만 감춤)
 * - POST /fo/events : 방문·샘플 조회 이벤트(fire-and-forget, 에러 조용히 삼킴)
 */
import { http, type SilentRequestConfig } from '#/api/http';
import { getSessionId } from '#/lib/sessionId';

/** 랜딩 샘플 뷰어에 노출할 대학 1건 */
export interface SampleMeta {
  /** 대학 식별자 */
  universityId: number;
  /** 대학명 */
  universityName: string;
  /** 샘플 콘텐츠 식별자(이벤트 추적용) */
  materialId: number;
  /** 샘플 PDF 존재 여부 */
  hasSample: boolean;
  /**
   * 표지 이미지 주소(서버 조립 — 066). 카드는 이 값을 가공 없이 `<img src>` 에 넣는다.
   * `?v=` 가 있으면 생성된 표지(1일 캐시), 없으면 미생성(그 요청이 서버 lazy 생성을 트리거).
   * 서버가 준 상대경로(`/api/...`)를 그대로 쓴다 — 모든 환경이 `VITE_API_BASE_URL=/api` 상대라
   * 동작하며, API 를 절대 URL(다른 origin)로 옮기는 날 이 가정을 재검토한다.
   */
  coverUrl: string;
}

/**
 * 랜딩 샘플 대학 목록 조회.
 *
 * 서버가 내려준 순서를 그대로 사용한다 — 노출 순서는 DB(`university.sample_sort_order`)가
 * 정하므로 여기서 재정렬하면 SSoT 가 둘로 갈라진다(FR-006).
 * @returns 대학 배열(없으면 빈 배열)
 */
export async function getLandingSamples(): Promise<SampleMeta[]> {
  const { data } = await http.get<SampleMeta[]>('/fo/landing/samples');
  return data;
}

/**
 * 대학 샘플 PDF 스트림 URL 반환.
 * pdf.js getDocument({ url }) 에 직접 전달한다 — **전체화면 뷰어(SampleViewerModal)만** 쓴다.
 * 카드 표지는 PDF 를 받지 않고 `SampleMeta.coverUrl`(서버 생성 이미지)을 쓴다(066).
 * http.ts 와 동일한 baseURL 해석(VITE_API_BASE_URL 또는 '/api').
 */
export function sampleStreamUrl(universityId: number): string {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
  return `${base}/fo/samples/${universityId}/stream`;
}

/** 랜딩 커버 섹션에 노출할 대학 1건(contracts/landing-coverage-api.md 소비 계약). */
export interface CoverageUniversity {
  /** 대학 표시명 */
  name: string;
  /** 샘플 열람 가능 여부(골드 강조 근거 — 서열 기준 아님) */
  sampleReady: boolean;
}

/**
 * 랜딩 커버 대학 목록 조회.
 *
 * `silent: true` 필수 — 실패해도 커버 섹션을 감추기만 하고 전역 에러 토스트는 띄우지
 * 않는다(FR-009). 미부착 시 http.ts 인터셉터가 실패를 곧바로 사용자 대면 토스트로
 * 노출해 랜딩 진입 경험을 해친다.
 * @returns 대학 배열(자격 대상 없으면 빈 배열)
 */
export async function getCoverageUniversities(): Promise<CoverageUniversity[]> {
  const { data } = await http.get<CoverageUniversity[]>('/fo/landing/universities', {
    silent: true,
  } as SilentRequestConfig);
  return data;
}

/**
 * 열람 이벤트 유형 — 백엔드 `EventType` enum 과 동기(041 contracts/events-extension.md).
 *
 * 발화 지점:
 * - `VISIT`: 랜딩 진입 1회
 * - `SAMPLE_VIEW`: 랜딩 샘플 뷰어 열림(materialId 필수)
 * - `SAMPLE_VIEW_EXPLORE`: 탐색(/materials) 미리보기 섹션의 샘플 뷰어 열림(materialId 필수)
 * - `PREVIEW_VIEW`: 같은 섹션의 발췌본 뷰어 열림(materialId 없음)
 * - `INTERVIEW_PREVIEW_VIEW`: 면접 로드맵 발췌본 뷰어 열림(#341 — 구매 화면·랜딩, materialId 없음)
 */
export type ViewEventType =
  | 'VISIT'
  | 'SAMPLE_VIEW'
  | 'SAMPLE_VIEW_EXPLORE'
  | 'PREVIEW_VIEW'
  | 'INTERVIEW_PREVIEW_VIEW';

/** POST /fo/events 요청 본문 */
interface EventBody {
  eventType: ViewEventType;
  sessionId: string;
  materialId?: number;
}

/**
 * 방문·샘플 조회 이벤트 전송(fire-and-forget).
 *
 * - silent: true → 인터셉터가 전역 토스트를 띄우지 않는다.
 * - try/catch 로 모든 에러를 조용히 삼킨다 — 렌더 흐름에 영향 없음.
 * @param eventType 이벤트 종류({@link ViewEventType})
 * @param materialId 샘플 콘텐츠 ID(SAMPLE_VIEW·SAMPLE_VIEW_EXPLORE 시 전달)
 */
export async function postEvent(eventType: ViewEventType, materialId?: number): Promise<void> {
  const body: EventBody = {
    eventType,
    sessionId: getSessionId(),
    ...(materialId !== undefined ? { materialId } : {}),
  };
  try {
    await http.post('/fo/events', body, { silent: true } as SilentRequestConfig);
  } catch {
    // best-effort: 이벤트 전송 실패는 사용자에게 노출하지 않는다
  }
}
